// Shared impact traversal core (ARCHITECTURE.md §8.2, §8.3).
//
// Both downstream ("what breaks if this changes?") and upstream ("what does
// this depend on?") traversals are the same breadth-first walk over the
// DependencyGraph edges; they differ only in which direction edges are
// followed:
//
//   downstream → follow *reverse* edges (if A imports B and B changes, A is
//                affected): index by edge.to, step to edge.from.
//   upstream   → follow *forward* edges (A depends on B): index by edge.from,
//                step to edge.to.
//
// For every reached node we record the BFS depth (distance from the changed
// set, 1 for direct neighbours) and the chain confidence — the minimum edge
// confidence along the path (§10.2: confidence degrades as candidate edges are
// traversed). Evidence accumulates along the path so each entry carries the
// provenance that justified it. Seed nodes are never reported as affected;
// they are the change set itself.

import type { EdgeClassification, GraphEdge } from "../types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { Evidence, EvidenceType } from "../evidence/types.js";
import {
  bestEvidence,
  computeRelevanceScore,
  mergeEvidence,
} from "../evidence/index.js";
import type { AffectedEntry } from "./types.js";

export type TraversalDirection = "downstream" | "upstream";

/** Default edge classifications traversed when `edgeTypes` is unspecified. */
export const DEFAULT_EDGE_TYPES: readonly EdgeClassification[] = [
  "deterministic",
  "candidate",
];

let minConfidenceDeprecationWarned = false;

/**
 * Emit the `minConfidence` deprecation warning at most once per process. Reset
 * via {@link resetMinConfidenceDeprecationWarning} (used by tests).
 */
function warnMinConfidenceDeprecated(): void {
  if (minConfidenceDeprecationWarned) return;
  minConfidenceDeprecationWarned = true;
  console.warn(
    "[agent-contracts-analyzer] `minConfidence` is deprecated (Issue #21): " +
      "confidence is now telemetry metadata. Use `edgeTypes` to select " +
      "deterministic/candidate edges instead.",
  );
}

/** Reset the once-per-process deprecation guard. Intended for tests. */
export function resetMinConfidenceDeprecationWarning(): void {
  minConfidenceDeprecationWarned = false;
}

export interface TraverseOptions {
  graph: DependencyGraph;
  /** Origin nodes (changed files for downstream, query files for upstream). */
  seeds: string[];
  direction: TraversalDirection;
  /** Maximum BFS depth to explore; default: unlimited. */
  maxDepth?: number;
  /**
   * Edge classifications to traverse; default: both. Passing only
   * `["deterministic"]` skips candidate edges entirely (fast path, Layer 1).
   */
  edgeTypes?: EdgeClassification[];
  /** Evidence types to exclude when deriving each entry's relevance score. */
  evidenceExclude?: EvidenceType[];
  /** Cap the number of returned entries (after sorting); default: unlimited. */
  limit?: number;
  /**
   * @deprecated Issue #21 — confidence is telemetry, not a filter. Use
   * `edgeTypes` instead. Still honoured for backward compatibility: drops paths
   * whose chain confidence falls below this.
   */
  minConfidence?: number;
}

/**
 * The confidence of a single edge: the strength of its strongest piece of
 * evidence. An edge with no evidence contributes confidence 0.
 */
function edgeConfidence(edge: GraphEdge): number {
  return bestEvidence(edge.evidence)?.confidence ?? 0;
}

interface Reached {
  depth: number;
  confidence: number;
  evidence: Evidence[];
}

/**
 * Breadth-first impact traversal. Returns one AffectedEntry per reached node
 * (excluding the seeds), sorted by depth ascending then path for stability.
 */
export function traverse(options: TraverseOptions): AffectedEntry[] {
  const {
    graph,
    seeds,
    direction,
    maxDepth,
    edgeTypes = DEFAULT_EDGE_TYPES,
    evidenceExclude,
    limit,
    minConfidence,
  } = options;

  // `minConfidence` is deprecated (Issue #21) but still honoured. A threshold
  // of 0 is a no-op, so only warn/filter when a meaningful value was passed.
  const confidenceFloor = minConfidence ?? 0;
  if (minConfidence !== undefined && minConfidence > 0) {
    warnMinConfidenceDeprecated();
  }

  // Classification filter: only traverse the requested edge kinds. Restricting
  // to `["deterministic"]` skips candidate edges (fast path, Layer 1).
  const allowedTypes = new Set<EdgeClassification>(edgeTypes);

  // Build the adjacency index in the traversal direction. `key` is the node we
  // arrive *at* when walking the graph; `neighbor` is where the step lands.
  const adjacency = new Map<string, { neighbor: string; edge: GraphEdge }[]>();
  for (const edge of graph.edges) {
    if (!allowedTypes.has(edge.classification)) continue;
    const key = direction === "downstream" ? edge.to : edge.from;
    const neighbor = direction === "downstream" ? edge.from : edge.to;
    const list = adjacency.get(key);
    if (list) list.push({ neighbor, edge });
    else adjacency.set(key, [{ neighbor, edge }]);
  }

  const seedSet = new Set(seeds);
  const best = new Map<string, Reached>();

  interface QueueItem {
    id: string;
    depth: number;
    confidence: number;
    evidence: Evidence[];
  }
  const queue: QueueItem[] = seeds.map((id) => ({
    id,
    depth: 0,
    confidence: 1,
    evidence: [],
  }));

  while (queue.length > 0) {
    const current = queue.shift() as QueueItem;
    if (maxDepth !== undefined && current.depth >= maxDepth) continue;

    for (const { neighbor, edge } of adjacency.get(current.id) ?? []) {
      // A changed file is never itself an "affected" entry.
      if (seedSet.has(neighbor)) continue;

      const nextDepth = current.depth + 1;
      const nextConfidence = Math.min(current.confidence, edgeConfidence(edge));
      // Deprecated backward-compat pruning: drop branches below the confidence
      // floor; an alternate, stronger path to the same node is still explored.
      if (nextConfidence < confidenceFloor) continue;
      const nextEvidence = mergeEvidence(current.evidence, edge.evidence);

      const existing = best.get(neighbor);
      const improves =
        existing === undefined ||
        nextDepth < existing.depth ||
        (nextDepth === existing.depth && nextConfidence > existing.confidence);
      if (!improves) continue;

      best.set(neighbor, {
        depth: nextDepth,
        confidence: nextConfidence,
        evidence: nextEvidence,
      });
      queue.push({
        id: neighbor,
        depth: nextDepth,
        confidence: nextConfidence,
        evidence: nextEvidence,
      });
    }
  }

  const entries: AffectedEntry[] = [];
  for (const [nodeId, reached] of best) {
    // A reached node may be a symbol (§5.2). The symbol's containing file is
    // used as `path` so file-level consumers keep working; symbol identity and
    // line range are attached when available from the underlying AST node.
    const astNode = graph.ast.nodes.get(nodeId);
    const symbolInfo =
      astNode?.kind === "symbol" ? astNode.symbolInfo : undefined;
    const path = symbolInfo && astNode ? astNode.filePath : nodeId;
    const artifactIds = graph.artifactMapping.get(path);

    const entry: AffectedEntry = {
      path,
      ...(artifactIds && artifactIds.length > 0
        ? { artifactId: artifactIds[0] }
        : {}),
      depth: reached.depth,
      evidence: reached.evidence,
      relevanceScore: computeRelevanceScore(reached.evidence, {
        ...(evidenceExclude ? { exclude: evidenceExclude } : {}),
      }),
    };
    if (symbolInfo) {
      entry.symbolId = symbolInfo.symbolId;
      entry.symbolName = symbolInfo.name;
      entry.line = symbolInfo.line;
      entry.endLine = symbolInfo.endLine;
    }
    entries.push(entry);
  }

  entries.sort(
    (a, b) =>
      a.depth - b.depth ||
      a.path.localeCompare(b.path) ||
      (a.symbolId ?? "").localeCompare(b.symbolId ?? ""),
  );

  // `limit` caps the result set. Applied after the (stable) sort so the
  // shallowest, most-relevant entries are retained.
  if (limit !== undefined && limit >= 0 && entries.length > limit) {
    return entries.slice(0, limit);
  }
  return entries;
}

/**
 * The chain confidence of an affected entry: the floor of all evidence
 * confidences accumulated along its path. Used to derive report-level
 * confidence. An entry without evidence is treated as fully confident.
 */
export function entryConfidence(entry: AffectedEntry): number {
  if (entry.evidence.length === 0) return 1;
  return entry.evidence.reduce((min, e) => Math.min(min, e.confidence), 1);
}
